// frontend/src/services/alarmSoundService.js
//
// Sonido de alarma GLOBAL — una sola instancia de Audio para toda la aplicación.
//
// Por qué un singleton de módulo y no un ref de componente:
//   - El componente que lo tenía se montaba en dos sitios (App y Panel de
//     Control), así que se creaban dos `Audio` distintos.
//   - Al navegar, el componente se desmontaba y el sonido se cortaba aunque la
//     alarma siguiera activa.
//
// Contrato: `sync(shouldPlay)` es IDEMPOTENTE. Da igual que haya 1 o 20 alarmas
// activas: si debe sonar y ya suena, no hace nada (no se reinicia ni se solapa).

const AUDIO_SRC = '/alarm.mp3';
const VOLUME = 0.6;

let audio = null;
let desired = false;          // lo que el sistema quiere: sonando o en silencio
let unlocked = false;         // ¿el navegador ya permite reproducir?
let unlockBound = false;
const listeners = new Set();

const notify = () => {
  const state = getState();
  listeners.forEach((listener) => {
    try {
      listener(state);
    } catch (_) {
      /* un suscriptor roto no puede tumbar a los demás */
    }
  });
};

const ensureAudio = () => {
  if (audio) return audio;
  try {
    audio = new Audio(AUDIO_SRC);
    audio.loop = true;              // un único loop continuo, no un pitido por alarma
    audio.volume = VOLUME;
    audio.preload = 'auto';
    audio.load();
  } catch (error) {
    console.warn('[alarm] No se pudo preparar el audio:', error);
    audio = null;
  }
  return audio;
};

/**
 * Los navegadores bloquean el autoplay hasta que hay interacción del usuario.
 * Se deja armado un listener único que, en cuanto el usuario toca algo, marca el
 * audio como desbloqueado y arranca si en ese momento debía estar sonando.
 */
const bindUnlock = () => {
  if (unlockBound || typeof document === 'undefined') return;
  unlockBound = true;

  const unlock = () => {
    unlocked = true;
    // Se libera el armado para poder volver a engancharlo si el navegador
    // vuelve a bloquear la reproducción más adelante (p. ej. otra pestaña).
    unlockBound = false;
    document.removeEventListener('click', unlock);
    document.removeEventListener('touchstart', unlock);
    document.removeEventListener('keydown', unlock);
    if (desired) play();
  };

  document.addEventListener('click', unlock, { once: true });
  document.addEventListener('touchstart', unlock, { once: true });
  document.addEventListener('keydown', unlock, { once: true });
};

const play = () => {
  const element = ensureAudio();
  if (!element) return;
  if (!element.paused) return;      // ya suena: no reiniciar
  const attempt = element.play();
  if (attempt && typeof attempt.catch === 'function') {
    attempt
      .then(() => { unlocked = true; notify(); })
      .catch(() => {
        // Autoplay bloqueado: queda pendiente hasta la primera interacción.
        unlocked = false;
        bindUnlock();
        notify();
      });
  }
};

const stop = () => {
  if (!audio || audio.paused) return;
  audio.pause();
  audio.currentTime = 0;
};

/** Estado del sonido, para que la UI pueda mostrarlo. */
export const getState = () => ({
  shouldPlay: desired,
  isPlaying: !!audio && !audio.paused,
  // true cuando hay alarma pero el navegador aún no permite sonar.
  blockedByBrowser: desired && !!audio && audio.paused && !unlocked,
});

/**
 * Única entrada del servicio: declara si DEBE sonar.
 * Llamarla en cada render/evento es seguro.
 */
export const syncAlarmSound = (shouldPlay) => {
  const next = !!shouldPlay;
  const changed = next !== desired;
  desired = next;

  if (next) {
    ensureAudio();
    bindUnlock();
    play();
  } else {
    stop();
  }
  if (changed) notify();
  return getState();
};

/** Prueba manual del sonido (botón "probar alarma"). */
export const testAlarmSound = async (milliseconds = 1500) => {
  const element = ensureAudio();
  if (!element) return false;
  try {
    await element.play();
    unlocked = true;
    if (!desired) {
      window.setTimeout(() => { if (!desired) stop(); }, milliseconds);
    }
    notify();
    return true;
  } catch (error) {
    bindUnlock();
    return false;
  }
};

export const subscribeAlarmSound = (listener) => {
  listeners.add(listener);
  listener(getState());
  return () => listeners.delete(listener);
};

export default { syncAlarmSound, testAlarmSound, subscribeAlarmSound, getState };
